'use client'

/**
 * ResumeBanner — sticky "return to tour" pill shown on destination pages
 * when the user has clicked "Take me there" from inside an Ashraya
 * prerequisite checklist.
 */

import { useEffect, useState, useCallback } from 'react'

const RESUME_KEY = 'ashraya:resume'
const RESUME_INTENT_KEY = 'ashraya:resumeIntent'

export function ResumeBanner({ theme }) {
  const [payload, setPayload] = useState(null)
  const [currentPath, setCurrentPath] = useState('')

  const readPayload = useCallback(() => {
    if (typeof window === 'undefined') return null
    try {
      const raw = sessionStorage.getItem(RESUME_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.tour || !parsed?.returnPath) return null
      // Safety expiry — anything older than 6 hours is stale.
      if (parsed.timestamp && Date.now() - parsed.timestamp > 6 * 60 * 60 * 1000) {
        sessionStorage.removeItem(RESUME_KEY)
        return null
      }
      return parsed
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    setCurrentPath(window.location.pathname + window.location.search)
    setPayload(readPayload())
    const t = setInterval(() => {
      setPayload(readPayload())
      setCurrentPath(window.location.pathname + window.location.search)
    }, 800)
    const onStorage = (e) => {
      if (e.key === RESUME_KEY) setPayload(readPayload())
    }
    window.addEventListener('storage', onStorage)
    return () => {
      clearInterval(t)
      window.removeEventListener('storage', onStorage)
    }
  }, [readPayload])

  if (!payload) return null
  if (currentPath === payload.returnPath) return null

  const primary = theme?.primary || '#f97316'
  const tourTitle = payload.tour?.title || 'tour'

  const handleReturn = () => {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.setItem(RESUME_INTENT_KEY, '1')
    } catch {}
    window.location.assign(payload.returnPath)
  }

  const handleDismiss = () => {
    if (typeof window === 'undefined') return
    try {
      sessionStorage.removeItem(RESUME_KEY)
      sessionStorage.removeItem(RESUME_INTENT_KEY)
    } catch {}
    setPayload(null)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 55,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          border: `1px solid ${primary}`,
          borderRadius: 999,
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
          padding: '8px 8px 8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: primary,
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
        <span style={{ fontSize: 13.5, color: '#0f172a', lineHeight: 1.3 }}>
          When you&apos;re done here, return to the{' '}
          <strong>{tourTitle}</strong> tour.
        </span>
        <button
          type="button"
          onClick={handleReturn}
          style={{
            background: primary,
            color: '#ffffff',
            border: 'none',
            borderRadius: 999,
            padding: '6px 14px',
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Return →
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          title="Dismiss — I'm not coming back to that tour"
          style={{
            background: 'transparent',
            color: '#94a3b8',
            border: 'none',
            borderRadius: '50%',
            width: 24,
            height: 24,
            fontSize: 14,
            lineHeight: 1,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  )
}

export default ResumeBanner
